const PG_STATEMENT_TIMEOUT = "57014";
const REPORT_RPC = "monitoring_report_db_timeout";
// The database keeps one alert per function per hour anyway; this keeps an
// incident from sending a report for every failing request.
const REPORT_INTERVAL_MS = 10 * 60 * 1000;
export const REPORT_TIMEOUT_MS = 2000;

export type DbTimeout = {
  rpc: string;
  args: Record<string, unknown>;
  durationMs: number;
  headers: Headers;
};

const lastReportAt = new Map<string, number>();

function takeReportSlot(rpc: string): boolean {
  const now = performance.now();
  const last = lastReportAt.get(rpc);
  if (last !== undefined && now - last < REPORT_INTERVAL_MS) return false;
  lastReportAt.set(rpc, now);
  return true;
}

// The only place that sees an RPC's name and arguments together with its
// response: a PostgrestError carries neither. The report is never awaited: a
// timeout usually means the database is struggling, and the failing request
// must not wait on a second call to it.
export function withDbTimeoutMonitoring(
  onTimeout: (timeout: DbTimeout) => void
): typeof fetch {
  // Cast: Bun's types add `fetch.preconnect`, which the clients never call.
  return (async (input: RequestInfo | URL, init?: RequestInit) => {
    // performance.now(), not Date.now(): this fetch also runs while Next
    // prerenders the link page, and Date.now() there is an error.
    const started = performance.now();
    const response = await fetch(input, init);
    if (response.ok) return response;

    const url = input instanceof Request ? input.url : String(input);
    const rpc = new URL(url).pathname.match(/\/rest\/v1\/rpc\/(\w+)$/)?.[1];
    if (!rpc || rpc === REPORT_RPC) return response;

    try {
      const body = await response.clone().json();
      if (body?.code === PG_STATEMENT_TIMEOUT && takeReportSlot(rpc)) {
        onTimeout({
          rpc,
          args: parseArgs(init?.body),
          durationMs: Math.round(performance.now() - started),
          headers: new Headers(init?.headers),
        });
      }
    } catch (error) {
      console.error("[db-timeout] report failed:", error);
    }
    return response;
  }) as typeof fetch;
}

function parseArgs(body: BodyInit | null | undefined): Record<string, unknown> {
  if (typeof body !== "string") return {};
  try {
    const parsed = JSON.parse(body);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
}

export function reportDbTimeoutFromBrowser({
  rpc,
  args,
  durationMs,
}: DbTimeout): void {
  void fetch("/api/monitoring/db-timeout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rpc, args, durationMs }),
    keepalive: true,
    signal: AbortSignal.timeout(REPORT_TIMEOUT_MS),
  }).catch((error) => console.error("[db-timeout] report failed:", error));
}
